// Helpers for xy_curve schema fields — a user-built relationship curve stored
// as an array of { x, y } points in schemaFields.

export interface XYCurvePoint {
  x: number;
  y: number;
}

export const sortCurvePoints = (points: XYCurvePoint[]): XYCurvePoint[] =>
  [...points].sort((a, b) => a.x - b.x);

// Extract the points array from a schemaFields value, tolerating both the
// standard { value: [...] } wrapper and a raw array.
export const curvePointsFromValue = (value: any): XYCurvePoint[] => {
  const raw = value && typeof value === 'object' && 'value' in value ? value.value : value;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (p) => p && typeof p === 'object' && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y))
  ).map((p) => ({ x: Number(p.x), y: Number(p.y) }));
};

// Parse pasted curve data: one point per line as "x,y". Blank lines are
// skipped; every malformed line is reported rather than silently dropped.
export const parseCurveCsv = (
  text: string
): { points: XYCurvePoint[]; errors: string[] } => {
  const points: XYCurvePoint[] = [];
  const errors: string[] = [];

  text.split(/\r?\n/).forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const parts = trimmed.split(',').map((s) => s.trim());
    if (parts.length !== 2 || parts.some((p) => p === '')) {
      errors.push(`Line ${index + 1}: expected "x,y" but got "${trimmed}"`);
      return;
    }

    const x = Number(parts[0]);
    const y = Number(parts[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      errors.push(`Line ${index + 1}: "${trimmed}" is not two numbers`);
      return;
    }

    points.push({ x, y });
  });

  return { points, errors };
};

// Axis display text: "Wind Speed (m/s)", "m/s", or "Wind Speed".
export const curveAxisTitle = (label?: string, unit?: string): string => {
  if (label && unit) return `${label} (${unit})`;
  return label || unit || '';
};
